import { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  authErrorAtom,
  authLoadingAtom,
  authTokensAtom,
  authUserAtom,
  isAuthenticatedAtom,
} from '../../atoms/authAtoms';
import * as authService from './authService';
import { queryClient } from '../../api/queryClient';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Authentication failed. Please try again.';
}

export function useAuth() {
  const [tokens, setTokens] = useAtom(authTokensAtom);
  const [user, setUser] = useAtom(authUserAtom);
  const [isLoading, setIsLoading] = useAtom(authLoadingAtom);
  const [error, setError] = useAtom(authErrorAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const signedInTokens = await authService.signIn(email, password);
        const signedInUser = authService.extractUserFromIdToken(signedInTokens.idToken);

        setTokens(signedInTokens);
        setUser(signedInUser);
      } catch (loginError) {
        setError(getErrorMessage(loginError));
        throw loginError;
      } finally {
        setIsLoading(false);
      }
    },
    [setError, setIsLoading, setTokens, setUser],
  );

  const logout = useCallback(async () => {
    try {
      await authService.signOut();
    } finally {
      queryClient.clear();
      setTokens(null);
      setUser(null);
      setError(null);
      setIsLoading(false);
    }
  }, [setError, setIsLoading, setTokens, setUser]);

  const restoreSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // fetchAuthSession (called inside getCurrentSession) automatically
      // refreshes expired tokens when a valid refresh token is stored.
      const sessionTokens = await authService.getCurrentSession();

      if (!sessionTokens) {
        setTokens(null);
        setUser(null);
        return;
      }

      setTokens(sessionTokens);
      setUser(authService.extractUserFromIdToken(sessionTokens.idToken));
    } catch (restoreError) {
      await logout();
      setError(getErrorMessage(restoreError));
    } finally {
      setIsLoading(false);
    }
  }, [logout, setError, setIsLoading, setTokens, setUser]);

  return {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    restoreSession,
  };
}
